import { commaListSchema, filterDateRangeSchema } from "@birdwatch/common";
import { z } from "zod";
import {
  type FilterUrlKeys,
  withOperators,
} from "@/features/shared/filters-new";

/**
 * The URL keys of one office Files tab: the filter system's (`search`,
 * `filters`, `filtersOrder`, `sort`) and the Display menu's — `layout`
 * (grid | list), `groupBy` (none | uploader, plus visit on the order tab), `sort` (`createdAt` =
 * oldest first) and `collapse` (folded groups).
 */
export type FileLibraryUrlKeys = FilterUrlKeys & {
  layout: string;
  groupBy: string;
  collapse: string;
};

/**
 * The place Files tab's keys. It is shown on the place detail and in the order
 * detail's aside place tabs — beside the order Files tab there — so it keeps
 * its own keys (kebab-case, like the order route's other keys).
 */
export const PLACE_FILES_URL_KEYS = {
  search: "place-files-search",
  filters: "place-files-filters",
  filtersOrder: "place-files-filters-order",
  sort: "place-files-sort",
  layout: "place-files-layout",
  groupBy: "place-files-group-by",
  collapse: "place-files-collapse",
} as const satisfies FileLibraryUrlKeys;

/**
 * The order Files tab's keys. The order page can show this tab beside the
 * aside's place Files tab, so it keeps its own keys and the two filter apart
 * (kebab-case, like the order route's other keys).
 */
export const ORDER_FILES_URL_KEYS = {
  search: "order-files-search",
  filters: "order-files-filters",
  filtersOrder: "order-files-filters-order",
  sort: "order-files-sort",
  layout: "order-files-layout",
  groupBy: "order-files-group-by",
  collapse: "order-files-collapse",
} as const satisfies FileLibraryUrlKeys;

// Every value is lenient (a strict `z.enum` throws `SearchParamError` and takes
// the page down on a stale link — webapp-routing.md); the tabs narrow them.
// A field missing here is stripped from the URL on validation, so its chip
// never shows — every field of `useFileLibraryFilterFields` needs its key.
const fileFilterSchemas = {
  source: withOperators(commaListSchema),
  type: withOperators(commaListSchema),
  categories: withOperators(commaListSchema),
  tags: withOperators(commaListSchema),
  uploader: withOperators(commaListSchema),
  uploaderRole: withOperators(commaListSchema),
  kind: withOperators(commaListSchema),
  room: withOperators(commaListSchema),
  system: withOperators(commaListSchema),
  equipment: withOperators(commaListSchema),
  condition: withOperators(commaListSchema),
  // A date range's operator is derived from the bounds it fills, so it is not
  // wrapped in `withOperators` (same as the quotes list's `createdAt`).
  uploadedAt: filterDateRangeSchema.catch(undefined),
};

/**
 * The place Files tab's URL keys, for the `validateSearch` of every route that
 * shows it — the place detail and the order detail. TanStack validates with the
 * current route's schema, so a route without these turns the comma lists back
 * into strings the filter chips cannot read.
 */
export const placeFilesSearchFragment = {
  [PLACE_FILES_URL_KEYS.search]: z.string().optional(),
  [PLACE_FILES_URL_KEYS.layout]: z.string().optional(),
  [PLACE_FILES_URL_KEYS.groupBy]: z.string().optional(),
  [PLACE_FILES_URL_KEYS.sort]: z.string().optional(),
  [PLACE_FILES_URL_KEYS.collapse]: commaListSchema,
  // The place tab has no Visits filter.
  [PLACE_FILES_URL_KEYS.filters]: z.object(fileFilterSchemas).optional(),
  [PLACE_FILES_URL_KEYS.filtersOrder]: z.string().optional(),
};

/** The order Files tab's URL keys, for the order detail's `validateSearch`. */
export const orderFilesSearchFragment = {
  [ORDER_FILES_URL_KEYS.search]: z.string().optional(),
  [ORDER_FILES_URL_KEYS.layout]: z.string().optional(),
  [ORDER_FILES_URL_KEYS.groupBy]: z.string().optional(),
  [ORDER_FILES_URL_KEYS.sort]: z.string().optional(),
  [ORDER_FILES_URL_KEYS.collapse]: commaListSchema,
  [ORDER_FILES_URL_KEYS.filters]: z
    .object({
      ...fileFilterSchemas,
      visit: withOperators(commaListSchema),
    })
    .optional(),
  [ORDER_FILES_URL_KEYS.filtersOrder]: z.string().optional(),
};

/**
 * The Upload from directory picker's keys. It opens over the order page — beside
 * both Files tabs — and over the schedule and the inbox, so it keeps keys of its
 * own. It is never grouped, so it has no grouping or folds.
 */
export const UPLOAD_FROM_DIRECTORY_URL_KEYS = {
  search: "upload-from-directory-search",
  filters: "upload-from-directory-filters",
  filtersOrder: "upload-from-directory-filters-order",
  sort: "upload-from-directory-sort",
  layout: "upload-from-directory-layout",
} as const satisfies FilterUrlKeys & Pick<FileLibraryUrlKeys, "layout">;

/**
 * The picker's URL keys, for the `/office` layout's `validateSearch`: every
 * office screen that can open it inherits them from there.
 */
export const uploadFromDirectorySearchFragment = {
  [UPLOAD_FROM_DIRECTORY_URL_KEYS.search]: z.string().optional(),
  [UPLOAD_FROM_DIRECTORY_URL_KEYS.sort]: z.string().optional(),
  [UPLOAD_FROM_DIRECTORY_URL_KEYS.layout]: z.string().optional(),
  // Every office screen parses this; a malformed value is dropped rather than
  // failing the whole layout route.
  // The picker reads the place's files, so like the place tab it has no Visits filter.
  [UPLOAD_FROM_DIRECTORY_URL_KEYS.filters]: z
    .object(fileFilterSchemas)
    .optional()
    .catch(undefined),
  [UPLOAD_FROM_DIRECTORY_URL_KEYS.filtersOrder]: z.string().optional(),
};
